// Lecture des raccourcis clavier du navigateur de bureau. Isolee de App pour
// etre testable sans monter l'application.
//
// Les chiffres se lisent sur e.code : sur un clavier AZERTY la rangee du haut
// donne & é " ' ( sans Maj, e.key n'y vaut jamais 1..7. Les lettres se lisent
// sur e.key, qui est la lettre imprimee sur la touche quelle que soit la
// disposition.

const DEGREE_BY_CODE = {};
for (let i = 1; i <= 7; i += 1) {
  DEGREE_BY_CODE[`Digit${i}`] = i - 1;
  DEGREE_BY_CODE[`Numpad${i}`] = i - 1;
}

const PAGE_BY_KEY = { h: 'home', j: 'learn', y: 'settings' };

// Champs de saisie : jamais de raccourci, dans tous les cas.
const TYPING_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="slider"]';

// Selecteur historique du handler de App, garde tel quel hors navigateur de bureau.
const LEGACY_SELECTOR = 'button, input, textarea, select, a[href], [contenteditable="true"], [role="slider"]';

const CONTROL_SELECTOR = 'button, a[href], [role="button"]';

// Les seules touches qu'un bouton ou un lien focalise consomme lui-meme : les
// laisser passer casserait la navigation au clavier. Une lettre ou un chiffre
// n'a aucune semantique native sur un bouton, elle passe toujours.
const CONTROL_KEYS = [' ', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/**
 * La cible de l'evenement interdit-elle le raccourci ?
 *
 * En mode assoupli (navigateur de bureau), un bouton ou un lien ne bloque que la
 * touche qu'il utiliserait lui-meme, et seulement s'il a le focus clavier. Le
 * :focus-visible seul ne suffit pas : Chrome promeut un bouton clique a la
 * souris des la premiere frappe, ce qui tuerait tous les raccourcis jusqu'au
 * clic suivant. jsdom ne connait pas :focus-visible et jette dessus : on laisse
 * alors passer le raccourci, c'est le comportement utile en test.
 */
export const isShortcutTargetBlocked = (target, { relaxed = false, key = '' } = {}) => {
  if (!target || typeof target.closest !== 'function') return false;
  if (!relaxed) return Boolean(target.closest(LEGACY_SELECTOR));
  if (target.closest(TYPING_SELECTOR)) return true;
  if (!CONTROL_KEYS.includes(key)) return false;
  const control = target.closest(CONTROL_SELECTOR);
  if (!control || typeof control.matches !== 'function') return false;
  try {
    return control.matches(':focus-visible');
  } catch (e) {
    return false;
  }
};

/**
 * Traduit un keydown en action, ou null si la touche ne nous concerne pas.
 * Maj reste autorisee : '?' en a besoin, et Maj+& vaut 1 sur AZERTY.
 */
export const resolveDesktopShortcut = (event) => {
  if (!event) return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.isComposing || event.repeat) return null;

  const degree = DEGREE_BY_CODE[event.code];
  // Pave numerique NumLock eteint : Numpad4 envoie ArrowLeft, Numpad2 ArrowDown.
  // Le code ne suffit donc pas la, on exige le chiffre.
  const numpadOff = event.code?.startsWith('Numpad') && !/^[1-7]$/.test(event.key);
  if (degree !== undefined && !numpadOff) return { type: 'degree', index: degree };

  if (event.key === '?') return { type: 'shortcuts' };
  if (event.key === 'ArrowLeft') return { type: 'keyStep', delta: -1 };
  if (event.key === 'ArrowRight') return { type: 'keyStep', delta: 1 };

  const k = typeof event.key === 'string' ? event.key.toLowerCase() : '';
  if (PAGE_BY_KEY[k]) return { type: 'page', page: PAGE_BY_KEY[k] };
  if (k === 's') return { type: 'neckView' };
  if (k === 't') return { type: 'chordExtension' };
  if (k === 'd') return { type: 'random' };
  if (k === 'v') return { type: 'mixer' };
  return null;
};

export const TOGGLE_NECK_VIEW_EVENT = 'upscale:toggle-neck-view';
export const TOGGLE_MIXER_EVENT = 'upscale:toggle-mixer';
export const TOGGLE_SHORTCUTS_EVENT = 'upscale:toggle-shortcuts';
